import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Gate } from '../../../global/contexts/auth/auth';
import { SEARCH_DEBOUNCE_MS } from '../../../core/constants/forms';
import { IconEdit, IconPlus } from '../../../common/icons';
import { useLabsList } from '../contexts/labs';
import SectionTabs from '../../navigation/section-tabs';
import { OPU_TABS } from '../../navigation/tabs';

import '../../../common/styles/catalog.css';

const STATUS_LABEL = { 2: 'Activo', 1: 'Inactivo', 0: 'Eliminado' };

export default function LabsOverview() {
    const [params, setParams] = useSearchParams();
    const urlSearch = params.get('q') || '';

    const [search, setSearch] = useState(urlSearch);
    const [debounced, setDebounced] = useState(urlSearch);

    useEffect(() => {
        const t = setTimeout(() => setDebounced(search), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [search]);

    useEffect(() => {
        const next = new URLSearchParams(params);
        if (debounced) next.set('q', debounced); else next.delete('q');
        setParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debounced]);

    const filters = useMemo(() => (debounced ? { q: debounced } : {}), [debounced]);
    const { data, isLoading, isFetching, error } = useLabsList(filters);
    const labs = data?.items ?? [];
    const total = data?.pagination?.total ?? labs.length;

    return (
        <div className="cat-overview">
            <SectionTabs tabs={OPU_TABS} />
            <header className="cat-overview__header">
                <div className="cat-overview__heading">
                    <h1 className="cat-overview__title">Laboratorios</h1>
                    <span className="cat-overview__count">{total}</span>
                </div>
                <Gate domain="labs" action="write">
                    <Link to="/admin/labs/new" className="cat-overview__add">
                        <IconPlus />
                        <span>Nuevo laboratorio</span>
                    </Link>
                </Gate>
            </header>

            <div className="cat-overview__toolbar">
                <div className="cat-overview__search">
                    <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Buscar por nombre o ubicación…"
                        aria-label="Buscar laboratorios por nombre o ubicación"
                    />
                </div>
            </div>

            {error && <div className="cat-overview__error">{error.message || 'No se pudieron cargar los laboratorios'}</div>}
            {total > labs.length && <p className="cat-overview__note">Mostrando los primeros {labs.length} de {total} laboratorios. Refiná la búsqueda.</p>}

            {isLoading ? (
                <div className="cat-overview__state">Cargando laboratorios…</div>
            ) : !labs.length ? (
                <div className="cat-overview__state">
                    <p className="cat-overview__state-text">
                        {debounced ? 'Ningún laboratorio coincide con la búsqueda.' : 'Aún no hay laboratorios.'}
                    </p>
                    {!debounced && (
                        <Gate domain="labs" action="write">
                            <Link to="/admin/labs/new" className="cat-overview__add">
                                <IconPlus /><span>Crear el primer laboratorio</span>
                            </Link>
                        </Gate>
                    )}
                </div>
            ) : (
                <div className={`cat-overview__card ${isFetching ? 'cat-overview__card--busy' : ''}`}>
                    <table className="cat-table">
                        <thead>
                            <tr>
                                <th>Nombre</th>
                                <th>Ubicación</th>
                                <th>Estado</th>
                                <th className="cat-table__actions-col" aria-label="Acciones" />
                            </tr>
                        </thead>
                        <tbody>
                            {labs.map((l) => (
                                <tr key={l.id} className="cat-table__row">
                                    <td>
                                        <Link to={`/admin/labs/${l.id}`} className="cat-table__name">{l.name}</Link>
                                    </td>
                                    <td>{l.location || <span className="cat-table__empty">—</span>}</td>
                                    <td><span className={`cat-badge cat-badge--${l.status}`}>{STATUS_LABEL[l.status] || '—'}</span></td>
                                    <td className="cat-table__actions">
                                        <Gate domain="labs" action="write">
                                            <Link to={`/admin/labs/${l.id}/edit`} className="cat-table__edit" aria-label={`Editar ${l.name}`} title="Editar">
                                                <IconEdit />
                                            </Link>
                                        </Gate>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
